import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../../../context/ThemeContext";
import { brand, brandTextColor } from "../../../config/theme";
import {
  useHikvisionAccessStore,
  fetchHikvisionDevices,
  createHikvisionDevice,
  updateHikvisionDevice,
  deleteHikvisionDevice,
  testHikvisionDevice,
  controlHikvisionDeviceSmart,
  setupHikvisionWebhook,
  firestoreInstantToIso,
} from "../../../store/oasis/hikvisionAccessStore";
import type {
  FirestoreInstant,
  HikvisionDevice,
  HikvisionConnectionMode,
  HikvisionReader,
  CreateHikvisionDeviceParams,
  UpdateHikvisionDeviceParams,
} from "../../../store/oasis/hikvisionAccessStore";
import { directionLanes } from "../accessSection/doorForDirection";
import { showDoorWaitingToast } from "../doorWaitingToast";
import {
  fetchTruckYardOptions,
  type TruckYardOption,
} from "../../../utils/truckYardOptions";
import Swal from "sweetalert2";
import { escapeHtml } from "../../../utils/escapeHtml";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ThemeClasses {
  bg: string;
  border: string;
  text: string;
  textSecondary: string;
  cardBg: string;
  cardBorder: string;
  buttonHover: string;
  buttonText: string;
  buttonTextHover: string;
}

interface DeviceFormData {
  device_serial: string;
  device_name: string;
  yard_id: string;
  door_no: string;
  door_type: "entry" | "exit" | "bidirectional";
  // Physical readers wired to this door. reader_no must match the controller's
  // cardReaderNo; direction decides entry/exit and wins over door_type.
  readers: HikvisionReader[];
  connection_mode: HikvisionConnectionMode;
  hikgateway_url: string;
  ip_address: string;
  username: string;
  password: string;
  device_port: string;
  status: "online" | "offline";
}

const EMPTY_FORM: DeviceFormData = {
  device_serial: "",
  device_name: "",
  yard_id: "",
  door_no: "1",
  door_type: "entry",
  readers: [{ reader_no: 1, name: "Lectora Entrada", direction: "entry" }],
  connection_mode: "hikgateway",
  hikgateway_url: "",
  ip_address: "",
  username: "",
  password: "",
  device_port: "80",
  status: "online",
};

// ─── Device Model Presets ────────────────────────────────────────────────────
// Pre-filled defaults for known Hikvision models. Selecting a model in the
// form fills connection-related fields (port, username, mode, door type).
// Sensitive fields like password / IP / serial are always left to the user.

interface DeviceModelPreset {
  id: string;
  label: string;
  description: string;
  defaults: Partial<DeviceFormData>;
}

// `label` / `description` hold i18n keys (namespace truckYard), resolved at
// render time inside DeviceFormModal via t().
const DEVICE_MODEL_PRESETS: DeviceModelPreset[] = [
  {
    id: "K1T341CMF-direct",
    label: "accessControl.presets.directLabel",
    description: "accessControl.presets.directDesc",
    defaults: {
      device_name: "Hikvision K1T341CMF",
      door_no: "1",
      door_type: "bidirectional",
      connection_mode: "direct",
      username: "admin",
      device_port: "80",
    },
  },
  {
    id: "K1T341CMF-relay",
    label: "accessControl.presets.relayLabel",
    description: "accessControl.presets.relayDesc",
    defaults: {
      device_name: "Hikvision K1T341CMF",
      door_no: "1",
      door_type: "bidirectional",
      connection_mode: "relay",
      username: "admin",
      device_port: "80",
    },
  },
  {
    id: "K1T341CMF-hikgateway",
    label: "accessControl.presets.hikgatewayLabel",
    description: "accessControl.presets.hikgatewayDesc",
    defaults: {
      device_name: "Hikvision K1T341CMF",
      door_no: "1",
      door_type: "bidirectional",
      connection_mode: "hikgateway",
    },
  },
  {
    id: "custom",
    label: "accessControl.presets.customLabel",
    description: "accessControl.presets.customDesc",
    defaults: {},
  },
];

const PAGE_SIZE = 12;

// ─── Status / tier helpers ───────────────────────────────────────────────────
// The badge color is computed from two independent signals:
//   1. Heartbeat liveness — "is the relay reporting this controller right now?"
//      For relay-mode devices we trust `connection_status`. For direct &
//      hikgateway we fall back to the legacy `status` field (the existing
//      15s direct-mode probe / manual toggle still drives it).
//   2. Last interaction age — time since the last card swipe or exit button
//      (`last_event_at`). Tiered: <1h green, <24h yellow, <48h orange, ≥48h red.
//
// When the heartbeat says disconnected we short-circuit to a dedicated red
// "Sin conexión" badge so the user can tell a physical disconnect from a door
// that simply hasn't been used in days.

type DeviceTier =
  | "probing"
  | "disconnected"
  | "online_idle"
  | "active"
  | "stale_1h"
  | "stale_24h"
  | "stale_48h";

function isControllerConnected(d: HikvisionDevice): boolean {
  if (d.connection_mode === "relay") {
    return d.connection_status === "connected";
  }
  // direct & hikgateway — no heartbeat signal yet, rely on legacy status
  return d.status === "online";
}

function deviceTier(d: HikvisionDevice, probing = false): DeviceTier {
  if (probing) return "probing";
  if (!isControllerConnected(d)) return "disconnected";
  // Connected (heartbeat live) but no card/exit event ever recorded — the
  // controller is healthy and online, just hasn't been used yet. Show it as
  // online rather than the alarming red "Sin uso" (which means stale, not dead).
  // Normalizado, no `new Date(d.last_event_at)` a secas: ese valor llega como
  // `{_seconds}` y daba `Invalid Date`, así que `ageMs` era NaN y TODAS las
  // comparaciones de abajo daban falso. El equipo caía siempre al peor tramo:
  // la pantalla reportaba mal la salud de controladores que estaban activos.
  const ultimoEvento = firestoreInstantToIso(d.last_event_at);
  if (!ultimoEvento) return "online_idle";
  const ageMs = Date.now() - new Date(ultimoEvento).getTime();
  if (ageMs < 60 * 60 * 1000) return "active";
  if (ageMs < 24 * 60 * 60 * 1000) return "stale_1h";
  if (ageMs < 48 * 60 * 60 * 1000) return "stale_24h";
  return "stale_48h";
}

function tierLabel(tier: DeviceTier, t: TFunction): string {
  switch (tier) {
    case "probing":      return t("accessControl.tier.probing");
    case "disconnected": return t("accessControl.tier.disconnected");
    case "online_idle":  return t("accessControl.tier.onlineIdle");
    case "active":       return t("accessControl.tier.active");
    case "stale_1h":     return t("accessControl.tier.stale1h");
    case "stale_24h":    return t("accessControl.tier.stale24h");
    case "stale_48h":    return t("accessControl.tier.stale48h");
  }
}

function tierClasses(t: DeviceTier): string {
  switch (t) {
    case "probing":
      return "bg-amber-500/15 text-amber-400 border border-amber-500/30";
    case "disconnected":
      return "bg-red-500/15 text-red-400 border border-red-500/30";
    case "online_idle":
      return "bg-green-500/15 text-green-400 border border-green-500/30";
    case "active":
      return "bg-green-500/15 text-green-400 border border-green-500/30";
    case "stale_1h":
      return "bg-yellow-500/15 text-yellow-400 border border-yellow-500/30";
    case "stale_24h":
      return "bg-orange-500/15 text-orange-400 border border-orange-500/30";
    case "stale_48h":
      return "bg-yellow-500/15 text-yellow-400 border border-yellow-500/30";
  }
}

function tierDot(t: DeviceTier): string {
  switch (t) {
    case "probing":      return "bg-amber-400 animate-pulse";
    case "disconnected": return "bg-red-400";
    case "online_idle":  return "bg-green-400";
    case "active":       return "bg-green-400";
    case "stale_1h":     return "bg-yellow-400";
    case "stale_24h":    return "bg-orange-400";
    case "stale_48h":    return "bg-yellow-400";
  }
}

// Relative time formatter — "hace 23m", "hace 4h", "hace 2d".
//
// Acepta el instante CRUDO del backend. `hikvisionDeviceList` devuelve
// `doc.data()` sin serializar, así que todo lo escrito con `serverTimestamp()`
// llega como `{_seconds,_nanoseconds}`. El guard de `Number.isNaN` de acá abajo
// tapaba el síntoma —pintaba "—"— pero el dato se perdía igual: esta pantalla
// mostraba "sin registro" sobre equipos que sí tenían último evento.
// Fecha corta de un instante crudo. El guard que había era `device.createdAt ?`,
// y un `{_seconds,_nanoseconds}` es un objeto: siempre truthy, así que nunca
// caía al "—" y pintaba "Invalid Date" en el detalle de todos los dispositivos.
// Comprobar la CONVERSIÓN y no la presencia es la única forma de que el guard
// signifique algo.
function fechaCorta(valor: FirestoreInstant | undefined): string {
  const iso = firestoreInstantToIso(valor);
  return iso ? new Date(iso).toLocaleDateString("es-MX") : "—";
}

function formatRelativeTime(valor: FirestoreInstant | undefined, t: TFunction): string {
  const iso = firestoreInstantToIso(valor);
  if (!iso) return t("accessControl.relativeTime.noRecord");
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "—";
  const ageMs = Date.now() - ts;
  if (ageMs < 0) return t("accessControl.relativeTime.now");
  const sec = Math.floor(ageMs / 1000);
  if (sec < 60) return t("accessControl.relativeTime.secondsAgo");
  const min = Math.floor(sec / 60);
  if (min < 60) return t("accessControl.relativeTime.minutesAgo", { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t("accessControl.relativeTime.hoursAgo", { count: hr });
  const day = Math.floor(hr / 24);
  if (day < 30) return t("accessControl.relativeTime.daysAgo", { count: day });
  return new Date(iso).toLocaleDateString("es-MX");
}

function doorTypeLabel(dt: HikvisionDevice["door_type"], t: TFunction) {
  if (dt === "entry") return t("accessControl.doorType.entry");
  if (dt === "exit") return t("accessControl.doorType.exit");
  return t("accessControl.doorType.bidirectional");
}

function doorTypeIcon(t: HikvisionDevice["door_type"]) {
  if (t === "entry") return "ri-login-box-line";
  if (t === "exit") return "ri-logout-box-line";
  return "ri-arrow-left-right-line";
}

function doorTypeColor(t: HikvisionDevice["door_type"]) {
  if (t === "entry") return "text-green-400";
  if (t === "exit") return "text-red-400";
  return "text-blue-400";
}

function doorTypeBg(t: HikvisionDevice["door_type"]) {
  if (t === "entry") return "bg-green-500/20";
  if (t === "exit") return "bg-red-500/20";
  return "bg-blue-500/20";
}

// ─── Header ──────────────────────────────────────────────────────────────────

function ControlPointsHeader({
  themeClasses,
  isDarkMode,
  searchQuery,
  selectedDoorType,
  selectedYard,
  yards,
  selectedView,
  onSearchChange,
  onDoorTypeChange,
  onYardChange,
  onViewChange,
  onAdd,
  total,
  loading,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  searchQuery: string;
  selectedDoorType: string;
  selectedYard: string;
  yards: TruckYardOption[];
  selectedView: "grid" | "list" | "console";
  onSearchChange: (v: string) => void;
  onDoorTypeChange: (v: string) => void;
  onYardChange: (v: string) => void;
  onViewChange: (v: "grid" | "list" | "console") => void;
  onAdd: () => void;
  total: number;
  loading: boolean;
}) {
  const { t } = useTranslation("truckYard");
  return (
    <div className={`${themeClasses.bg} border-b ${themeClasses.border} p-4 sm:p-6`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <p className={`text-xs ${themeClasses.textSecondary} mb-0.5`}>{t("common.breadcrumb")}</p>
          <h1 className={`text-xl font-bold ${themeClasses.text}`}>
            {t("accessControl.title")}
            {!loading && <span className={`text-sm font-normal ${themeClasses.textSecondary} ml-2`}>({total})</span>}
          </h1>
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            {t("accessControl.subtitle")}
          </p>
        </div>
        <button
          onClick={onAdd}
          className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm transition-colors"
        >
          <i className="ri-add-line"></i>
          <span>{t("accessControl.addButton")}</span>
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className={`relative flex items-center ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} rounded-xl px-3 py-2 gap-2 flex-1`}>
          <i className={`ri-search-line text-sm ${themeClasses.textSecondary}`}></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("accessControl.searchPlaceholder")}
            className={`bg-transparent outline-none text-sm ${themeClasses.text} w-full`}
          />
        </div>

        <div className={`relative ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} rounded-xl`}>
          <select
            value={selectedDoorType}
            onChange={(e) => onDoorTypeChange(e.target.value)}
            className={`appearance-none px-3 py-2.5 pr-8 bg-transparent outline-none text-sm ${themeClasses.text} cursor-pointer w-full sm:w-auto`}
          >
            <option value="">{t("accessControl.allTypes")}</option>
            <option value="entry">{t("accessControl.doorType.entry")}</option>
            <option value="exit">{t("accessControl.doorType.exit")}</option>
            <option value="bidirectional">{t("accessControl.doorType.bidirectional")}</option>
          </select>
          <i className={`ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-sm ${themeClasses.textSecondary} pointer-events-none`}></i>
        </div>

        <div className={`relative ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} rounded-xl`}>
          <select
            value={selectedYard}
            onChange={(e) => onYardChange(e.target.value)}
            className={`appearance-none px-3 py-2.5 pr-8 bg-transparent outline-none text-sm ${themeClasses.text} cursor-pointer w-full sm:w-auto`}
            title={t("accessControl.filterByYard")}
          >
            <option value="">{t("accessControl.allYards")}</option>
            {yards.length === 0 && <option value="" disabled>{t("accessControl.noYards")}</option>}
            {yards.map((y) => (
              <option key={y.id} value={y.id}>{y.name}</option>
            ))}
          </select>
          <i className={`ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-sm ${themeClasses.textSecondary} pointer-events-none`}></i>
        </div>

        <div className={`flex ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} rounded-lg p-1 gap-1`}>
          <button
            onClick={() => onViewChange("console")}
            className={`p-1.5 rounded-md transition-colors ${
              selectedView === "console"
                ? isDarkMode ? "bg-dark-surface text-white" : "bg-white text-gray-900 shadow-sm"
                : themeClasses.buttonText
            }`}
            title="Vista jerarquía (controladora → puertas → lectoras)"
          >
            <i className="ri-node-tree text-lg"></i>
          </button>
          <button
            onClick={() => onViewChange("grid")}
            className={`p-1.5 rounded-md transition-colors ${
              selectedView === "grid"
                ? isDarkMode ? "bg-dark-surface text-white" : "bg-white text-gray-900 shadow-sm"
                : themeClasses.buttonText
            }`}
            title={t("common.gridView")}
          >
            <i className="ri-grid-line text-lg"></i>
          </button>
          <button
            onClick={() => onViewChange("list")}
            className={`p-1.5 rounded-md transition-colors ${
              selectedView === "list"
                ? isDarkMode ? "bg-dark-surface text-white" : "bg-white text-gray-900 shadow-sm"
                : themeClasses.buttonText
            }`}
            title={t("common.listView")}
          >
            <i className="ri-list-unordered text-lg"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Grid Card ───────────────────────────────────────────────────────────────

function DeviceGridCard({
  device,
  themeClasses,
  isDarkMode,
  probing = false,
  onDetails,
  onEdit,
}: {
  device: HikvisionDevice;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  probing?: boolean;
  onDetails: (d: HikvisionDevice) => void;
  onEdit: (d: HikvisionDevice) => void;
}) {
  const { t } = useTranslation("truckYard");
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const tier = deviceTier(device, probing);
  const disconnected = tier === "disconnected";

  return (
    <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-2xl p-5 hover:shadow-lg transition-all duration-200`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${doorTypeBg(device.door_type)}`}>
            <i className={`${doorTypeIcon(device.door_type)} ${doorTypeColor(device.door_type)} text-xl`}></i>
          </div>
          <div className="min-w-0">
            <p className={`font-semibold text-sm ${themeClasses.text} truncate`}>{device.device_name}</p>
            <span className={`text-xs font-mono ${themeClasses.textSecondary}`}>{device.device_serial}</span>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${tierClasses(tier)}`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${tierDot(tier)}`} />
          {tierLabel(tier, t)}
        </span>
      </div>

      {/* Truck-yard pill */}
      <div className="mb-3">
        <span
          className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium max-w-full ${isDarkMode ? "bg-white/5 text-gray-200" : "bg-gray-100 text-gray-700"}`}
          title={device.yard_name || t("accessControl.card.noYard")}
        >
          <i className="ri-map-pin-line text-[11px] flex-shrink-0" style={{ color: brandTextColor }}></i>
          <span className="truncate">{device.yard_name || t("accessControl.card.noYard")}</span>
        </span>
      </div>

      <div className={`${surface} rounded-xl p-3 space-y-2 mb-4`}>
        <div className="flex items-center gap-2">
          <i className={`ri-door-open-line text-xs ${themeClasses.textSecondary}`}></i>
          <span className={`text-xs ${themeClasses.textSecondary}`}>{t("accessControl.card.doorLabel")} #{device.door_no} · {doorTypeLabel(device.door_type, t)}</span>
        </div>
        {device.ip_address && (
          <div className="flex items-center gap-2">
            <i className={`ri-global-line text-xs ${themeClasses.textSecondary}`}></i>
            <span className={`text-xs font-mono ${themeClasses.textSecondary}`}>{device.ip_address}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <i className={`ri-time-line text-xs ${themeClasses.textSecondary}`}></i>
          <span className={`text-xs ${themeClasses.textSecondary}`}>
            {t("accessControl.card.lastInteraction")}: {formatRelativeTime(device.last_event_at, t)}
          </span>
        </div>
        {disconnected && (
          <div className="flex items-center gap-2">
            <i className="ri-alert-line text-xs text-red-400"></i>
            <span className="text-xs text-red-400">
              {device.connection_mode === "relay"
                ? `${t("accessControl.card.relayNotReporting")}${device.connection_lost_at ? ` ${formatRelativeTime(device.connection_lost_at, t)}` : ""}`
                : t("accessControl.card.deviceNotResponding")}
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onDetails(device)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all"
          style={{ backgroundColor: brand.primary, color: brand.accent }}
        >
          <i className="ri-eye-line text-sm"></i>
          {t("common.details")}
        </button>
        <button
          onClick={() => onEdit(device)}
          className={`px-3 py-2 rounded-xl text-xs font-medium transition-all border ${themeClasses.cardBorder} ${themeClasses.buttonText} ${themeClasses.buttonHover}`}
        >
          <i className="ri-pencil-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

// ─── List Row ────────────────────────────────────────────────────────────────

function DeviceListRow({
  device,
  themeClasses,
  probing = false,
  onDetails,
  onEdit,
}: {
  device: HikvisionDevice;
  themeClasses: ThemeClasses;
  probing?: boolean;
  onDetails: (d: HikvisionDevice) => void;
  onEdit: (d: HikvisionDevice) => void;
}) {
  const { t } = useTranslation("truckYard");
  const tier = deviceTier(device, probing);
  return (
    <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-md transition-all duration-200`}>
      {/* Mobile */}
      <div className="flex items-center gap-3 sm:hidden">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${doorTypeBg(device.door_type)}`}>
          <i className={`${doorTypeIcon(device.door_type)} ${doorTypeColor(device.door_type)} text-lg`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm ${themeClasses.text} truncate`}>{device.device_name}</p>
          <p className={`text-xs ${themeClasses.textSecondary}`}>{device.device_serial} · {t("accessControl.card.doorLabel")} #{device.door_no}</p>
          <p className={`text-[11px] ${themeClasses.textSecondary} mt-0.5`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${tierDot(tier)}`} />
            {tierLabel(tier, t)} · {formatRelativeTime(device.last_event_at, t)}
          </p>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={() => onDetails(device)} className="w-8 h-8 rounded-full flex items-center justify-center transition-colors" style={{ backgroundColor: brand.primary }}>
            <i className="ri-eye-line text-white text-sm"></i>
          </button>
          <button onClick={() => onEdit(device)} className={`w-8 h-8 rounded-full flex items-center justify-center ${themeClasses.buttonHover} border ${themeClasses.cardBorder}`}>
            <i className={`ri-pencil-line text-sm ${themeClasses.buttonText}`}></i>
          </button>
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden sm:flex items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${doorTypeBg(device.door_type)}`}>
            <i className={`${doorTypeIcon(device.door_type)} ${doorTypeColor(device.door_type)} text-lg`}></i>
          </div>
          <div className="min-w-0">
            <p className={`font-semibold text-sm ${themeClasses.text} truncate`}>{device.device_name}</p>
            <span className={`text-xs font-mono ${themeClasses.textSecondary}`}>{device.device_serial}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="hidden md:block text-center min-w-[120px]">
            <p className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}>{t("accessControl.list.yard")}</p>
            <p className={`text-sm ${themeClasses.text} truncate max-w-[120px]`}>{device.yard_name || "—"}</p>
          </div>
          <div className="hidden lg:block text-center min-w-[80px]">
            <p className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}>{t("accessControl.list.door")}</p>
            <p className={`text-xs ${themeClasses.text}`}>#{device.door_no} · {doorTypeLabel(device.door_type, t)}</p>
          </div>
          <div className="hidden xl:block text-center min-w-[100px]">
            <p className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}>{t("accessControl.list.ip")}</p>
            <p className={`text-xs font-mono ${themeClasses.text}`}>{device.ip_address || "—"}</p>
          </div>

          <div className="hidden lg:block text-center min-w-[110px]">
            <p className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}>{t("accessControl.list.lastInteraction")}</p>
            <p className={`text-xs ${themeClasses.text}`}>{formatRelativeTime(device.last_event_at, t)}</p>
          </div>

          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${tierClasses(tier)}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${tierDot(tier)}`} />
            {tierLabel(tier, t)}
          </span>

          <div className="flex gap-1">
            <button
              onClick={() => onDetails(device)}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
              style={{ backgroundColor: brand.primary }}
              title={t("accessControl.viewDetails")}
            >
              <i className="ri-eye-line text-white text-sm"></i>
            </button>
            <button
              onClick={() => onEdit(device)}
              className={`w-8 h-8 rounded-full flex items-center justify-center ${themeClasses.buttonHover} border ${themeClasses.cardBorder}`}
              title={t("common.edit")}
            >
              <i className={`ri-pencil-line text-sm ${themeClasses.buttonText}`}></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Console (controller) hierarchy ──────────────────────────────────────────
// Door docs that share a `device_serial` are doors on the same physical
// controller (console). The diagram models Console → Doors → Readers, so we
// derive the console grouping from the flat door list and render it as a tree.

interface ConsoleGroup {
  serial: string;
  model: string | null;
  ip: string | null;
  yardName: string | null;
  connectionMode: HikvisionConnectionMode;
  doors: HikvisionDevice[];
  connected: boolean;
  lastHeartbeat: string | null | undefined;
}

function groupByConsole(devices: HikvisionDevice[]): ConsoleGroup[] {
  const map = new Map<string, ConsoleGroup>();
  for (const d of devices) {
    let g = map.get(d.device_serial);
    if (!g) {
      g = {
        serial: d.device_serial,
        model: d.controller_model ?? null,
        ip: d.ip_address ?? null,
        yardName: d.yard_name ?? null,
        connectionMode: d.connection_mode,
        doors: [],
        connected: false,
        lastHeartbeat: null,
      };
      map.set(d.device_serial, g);
    }
    g.doors.push(d);
    // The console counts as connected if any of its doors reports connected
    // (they all share one heartbeat keyed by serial).
    if (isControllerConnected(d)) g.connected = true;
    const hb = firestoreInstantToIso(d.last_heartbeat_at);
    if (hb && (!g.lastHeartbeat || new Date(hb) > new Date(g.lastHeartbeat))) {
      g.lastHeartbeat = hb;
    }
    if (!g.model && d.controller_model) g.model = d.controller_model;
    if (!g.ip && d.ip_address) g.ip = d.ip_address;
  }
  for (const g of map.values()) g.doors.sort((a, b) => a.door_no - b.door_no);
  return Array.from(map.values()).sort((a, b) =>
    (a.model || a.serial).localeCompare(b.model || b.serial),
  );
}

function ConsoleCard({
  group,
  themeClasses,
  isDarkMode,
  probingIds,
  onDetails,
  onEdit,
}: {
  group: ConsoleGroup;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  probingIds: Set<string>;
  onDetails: (d: HikvisionDevice) => void;
  onEdit: (d: HikvisionDevice) => void;
}) {
  const { t } = useTranslation("truckYard");
  const [expanded, setExpanded] = useState(true);
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const doorCount = group.doors.length;
  const readerCount = group.doors.reduce(
    (n, d) => n + (d.readers?.length ?? 0),
    0,
  );

  return (
    <div className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-2xl overflow-hidden`}>
      {/* Console header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className={`w-full flex items-center gap-3 p-4 text-left ${themeClasses.buttonHover} transition-colors`}
      >
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary-500/15">
          <i className="ri-cpu-line text-primary-400 text-xl"></i>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`font-semibold text-sm ${themeClasses.text} truncate`}>
              {group.model || "Controladora"}
            </p>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                group.connected
                  ? "bg-green-500/15 text-green-400 border border-green-500/30"
                  : "bg-red-500/15 text-red-400 border border-red-500/30"
              }`}
            >
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${
                  group.connected ? "bg-green-400" : "bg-red-400"
                }`}
              />
              {group.connected ? "En línea" : "Sin conexión"}
            </span>
            {group.yardName && (
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${isDarkMode ? "bg-white/5 text-gray-200" : "bg-gray-100 text-gray-700"}`}>
                <i className="ri-map-pin-line text-[11px]" style={{ color: brandTextColor }}></i>
                {group.yardName}
              </span>
            )}
          </div>
          <p className={`text-xs ${themeClasses.textSecondary} font-mono truncate`}>
            {group.serial}
            {group.ip ? ` · ${group.ip}` : ""}
          </p>
          <p className={`text-[11px] ${themeClasses.textSecondary}`}>
            {doorCount} puerta
            {doorCount !== 1 ? "s" : ""} · {readerCount} lectora
            {readerCount !== 1 ? "s" : ""}
            {group.connected && group.lastHeartbeat
              ? ` · beat ${formatRelativeTime(group.lastHeartbeat, t)}`
              : ""}
          </p>
        </div>
        <i
          className={`ri-arrow-down-s-line text-lg ${themeClasses.textSecondary} transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        ></i>
      </button>

      {/* Doors → Readers */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {group.doors.map((door) => {
            const tier = deviceTier(door, probingIds.has(door.id));
            return (
              <div key={door.id} className={`${surface} rounded-xl p-3`}>
                {/* Door header */}
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${doorTypeBg(door.door_type)}`}>
                    <i className={`ri-door-open-line ${doorTypeColor(door.door_type)} text-base`}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${themeClasses.text} truncate`}>{door.device_name}</p>
                    <p className={`text-[11px] ${themeClasses.textSecondary}`}>
                      Puerta #{door.door_no} · {doorTypeLabel(door.door_type, t)} · {formatRelativeTime(door.last_event_at, t)}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${tierClasses(tier)}`}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${tierDot(tier)}`} />
                    {tierLabel(tier, t)}
                  </span>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => onDetails(door)} className="w-7 h-7 rounded-full flex items-center justify-center transition-colors" style={{ backgroundColor: brand.primary }} title="Detalles">
                      <i className="ri-eye-line text-white text-xs"></i>
                    </button>
                    <button onClick={() => onEdit(door)} className={`w-7 h-7 rounded-full flex items-center justify-center ${themeClasses.buttonHover} border ${themeClasses.cardBorder}`} title="Editar">
                      <i className={`ri-pencil-line text-xs ${themeClasses.buttonText}`}></i>
                    </button>
                  </div>
                </div>

                {/* Readers (lectoras) */}
                {door.readers && door.readers.length > 0 ? (
                  <div className="mt-2.5 ml-10 space-y-1.5 border-l border-dashed border-gray-500/25 pl-3">
                    {door.readers.map((r) => (
                      <div key={r.reader_no} className="flex items-center gap-2">
                        <i className={`${r.direction === "entry" ? "ri-login-box-line text-green-400" : "ri-logout-box-line text-red-400"} text-sm`}></i>
                        <span className={`text-xs ${themeClasses.text}`}>{r.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.direction === "entry" ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"}`}>
                          {r.direction === "entry" ? "Entrada" : "Salida"}
                        </span>
                        <span className={`text-[10px] ${themeClasses.textSecondary} font-mono`}>#{r.reader_no}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`mt-2 ml-10 text-[11px] ${themeClasses.textSecondary} italic`}>
                    Sin lectoras configuradas
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Details Modal ───────────────────────────────────────────────────────────

function DeviceDetailsModal({
  device,
  themeClasses,
  isDarkMode,
  probing = false,
  onClose,
  onEdit,
  onDelete,
}: {
  device: HikvisionDevice;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  probing?: boolean;
  onClose: () => void;
  onEdit: (d: HikvisionDevice) => void;
  onDelete: (d: HikvisionDevice) => void;
}) {
  const { t } = useTranslation("truckYard");
  const navigate = useNavigate();
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const labelColor = isDarkMode ? "text-gray-400" : "text-gray-500";
  const isDirect = device.connection_mode === "direct";
  const isRelay = device.connection_mode === "relay";
  const usesIsapi = isDirect || isRelay; // both modes ultimately speak ISAPI to the device

  const tier = deviceTier(device, probing);

  // ── Direct mode actions ─────────────────────────────────────────────────
  // Abrir/cerrar llevan la puerta en la clave ("open:2") para que, con un
  // carril por puerta, el spinner salga sólo en el botón que se tocó. Con una
  // sola pluma la puerta es `door_no`, así que la clave nunca queda vacía.
  const [busy, setBusy] = useState<null | "test" | "webhook" | `${"open" | "close"}:${number}`>(null);
  const lanes = useMemo(() => directionLanes(device), [device]);
  // Reintentos por "SDK busy" en vuelo: al cerrar el modal se cortan, para que
  // la pluma no se mueva cuando ya nadie la mira.
  const doorAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => doorAbortRef.current?.abort(), []);
  // Un par Abrir/Cerrar por pluma. `door` undefined = la de siempre (door_no,
  // sin mandarla); con varias puertas cada par manda la suya.
  const controlPairs: { door?: number; label: string | null }[] = lanes.length > 0
    ? lanes.map((l) => ({
        door: l.door,
        label: t("accessControl.control.laneDoor", {
          door: l.door,
          lane: l.directions
            .map((d) => t(d === "entry" ? "accessControl.control.laneEntry" : "accessControl.control.laneExit"))
            .join(" / "),
        }),
      }))
    : [{ door: undefined, label: null }];

  const handleTest = async () => {
    setBusy("test");
    try {
      const result = await testHikvisionDevice(device.id);
      if (result.success) {
        const info = result.data || {};
        Swal.fire({
          icon: "success",
          title: t("accessControl.actions.deviceOnline"),
          html: `<div class="text-left text-sm">
            <p><strong>${t("accessControl.actions.model")}:</strong> ${escapeHtml(String(info.model || "—"))}</p>
            <p><strong>${t("accessControl.actions.serial")}:</strong> ${escapeHtml(String(info.serialNumber || "—"))}</p>
            <p><strong>${t("accessControl.actions.firmware")}:</strong> ${escapeHtml(String(info.firmwareVersion || "—"))}</p>
            <p><strong>${t("accessControl.actions.mac")}:</strong> ${escapeHtml(String(info.macAddress || "—"))}</p>
          </div>`,
        });
      } else {
        Swal.fire({ icon: "error", title: t("accessControl.actions.couldNotConnect"), text: result.error || "DeviceUnreachable" });
      }
    } catch (err: unknown) {
      Swal.fire({ icon: "error", title: t("common.error"), text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  // `door` sólo viene en un dispositivo de varias puertas (Acuapue). Sin ella
  // el backend usa el `door_no` del documento, como siempre.
  const handleControl = async (action: "open" | "close", door?: number) => {
    setBusy(`${action}:${door ?? device.door_no}`);
    const abort = new AbortController();
    doorAbortRef.current = abort;
    try {
      const result = await controlHikvisionDeviceSmart(
        { id: device.id, connection_mode: device.connection_mode },
        action,
        {
          door_no: door,
          // "SDK busy": el store reintenta; el botón sigue girando y un toast
          // dice que se espera turno, para que no se lea como "no respondió".
          onBusyRetry: (attempt) => showDoorWaitingToast(t, attempt),
          signal: abort.signal,
        },
      );
      // Modal cerrado a mitad de los reintentos: nada que mostrar ni estado que tocar.
      if (abort.signal.aborted) return;
      if (result.success) {
        // Con varias plumas el toast dice cuál se movió: si el mapa estuviera
        // mal, "Puerta 1 abierta" tras tocar la entrada lo delata en el acto.
        const title = action === "open" ? t("accessControl.actions.doorOpened") : t("accessControl.actions.doorClosed");
        Swal.fire({
          icon: "success",
          title: door !== undefined ? `${title} · #${door}` : title,
          timer: 1800,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", title: t("accessControl.actions.commandRejected"), text: result.error || "DeviceRejected" });
      }
    } catch (err: unknown) {
      Swal.fire({ icon: "error", title: t("common.error"), text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const handleSetupWebhook = async () => {
    const { value: url } = await Swal.fire({
      title: t("accessControl.webhook.title"),
      input: "text",
      inputLabel: t("accessControl.webhook.inputLabel"),
      inputPlaceholder: "https://example.com/hikvisionWebhook",
      showCancelButton: true,
      confirmButtonText: t("accessControl.webhook.confirm"),
      cancelButtonText: t("common.cancel"),
    });
    if (url === undefined) return; // cancelled

    setBusy("webhook");
    try {
      const result = await setupHikvisionWebhook({
        id: device.id,
        webhook_url: url ? String(url).trim() : undefined,
      });
      if (result.success) {
        Swal.fire({
          icon: "success",
          title: t("accessControl.webhook.configuredTitle"),
          text: t("accessControl.webhook.configuredText", { url: result.webhook_url }),
        });
      } else {
        Swal.fire({ icon: "error", title: t("accessControl.webhook.failedTitle"), text: result.error || "DeviceRejected" });
      }
    } catch (err: unknown) {
      Swal.fire({ icon: "error", title: t("common.error"), text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto`}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>{t("accessControl.details.title")}</h2>
          <button onClick={onClose} className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}>
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Device header */}
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${doorTypeBg(device.door_type)}`}>
              <i className={`${doorTypeIcon(device.door_type)} ${doorTypeColor(device.door_type)} text-2xl`}></i>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={`text-base font-bold ${themeClasses.text}`}>{device.device_name}</h3>
              <span className={`text-xs font-mono ${themeClasses.textSecondary}`}>{device.device_serial}</span>
            </div>
            <span className={`text-xs px-2.5 py-1.5 rounded-full font-medium ${tierClasses(tier)}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${tierDot(tier)}`} />
              {tierLabel(tier, t)}
            </span>
          </div>

          {/* Info grid */}
          <div className={`${surface} rounded-xl p-4 grid grid-cols-2 gap-4`}>
            <div>
              <p className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}>{t("accessControl.details.yard")}</p>
              <p className={`text-sm ${themeClasses.text}`}>{device.yard_name || "—"}</p>
            </div>
            <div>
              <p className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}>{t("accessControl.details.door")}</p>
              <p className={`text-sm ${themeClasses.text}`}>#{device.door_no}</p>
            </div>
            <div>
              <p className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}>{t("accessControl.details.doorType")}</p>
              <p className={`text-sm ${themeClasses.text} flex items-center gap-1`}>
                <i className={`${doorTypeIcon(device.door_type)} ${doorTypeColor(device.door_type)}`}></i>
                {doorTypeLabel(device.door_type, t)}
              </p>
            </div>
            <div>
              <p className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}>{t("accessControl.details.deviceIp")}</p>
              <p className={`text-sm font-mono ${themeClasses.text}`}>{device.ip_address || t("accessControl.details.notConfigured")}</p>
            </div>
          </div>

          {/* Connection details (mode-aware) */}
          <div className={`${surface} rounded-xl p-4`}>
            <p className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor} mb-3 flex items-center justify-between`}>
              <span>
                <i className={`${isDirect ? "ri-plug-line" : isRelay ? "ri-links-line" : "ri-cloud-line"} mr-1`}></i>
                {isDirect ? t("accessControl.connection.directTitle") : isRelay ? t("accessControl.connection.relayTitle") : t("accessControl.connection.hikgatewayTitle")}
              </span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                isDirect
                  ? "border-blue-500/30 bg-blue-500/15 text-blue-400"
                  : isRelay
                    ? "border-amber-500/30 bg-amber-500/15 text-amber-400"
                    : "border-purple-500/30 bg-purple-500/15 text-purple-400"
              }`}>
                {isDirect ? t("accessControl.connection.directBadge") : isRelay ? t("accessControl.connection.relayBadge") : t("accessControl.connection.hikgatewayBadge")}
              </span>
            </p>
            <div className="space-y-2">
              {usesIsapi ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className={`text-[11px] ${labelColor} font-medium mb-0.5`}>{t("accessControl.connection.username")}</p>
                      <p className={`text-sm font-mono ${themeClasses.text}`}>{device.username || "—"}</p>
                    </div>
                    <div>
                      <p className={`text-[11px] ${labelColor} font-medium mb-0.5`}>{t("accessControl.connection.port")}</p>
                      <p className={`text-sm font-mono ${themeClasses.text}`}>{device.device_port || 80}</p>
                    </div>
                  </div>
                  {device.webhook_target_url && (
                    <div>
                      <p className={`text-[11px] ${labelColor} font-medium mb-0.5`}>{t("accessControl.connection.webhookConfigured")}</p>
                      <p className={`text-sm font-mono ${themeClasses.text} break-all`}>{device.webhook_target_url}</p>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <p className={`text-[11px] ${labelColor} font-medium mb-0.5`}>{t("accessControl.connection.serverUrl")}</p>
                  <p className={`text-sm font-mono ${themeClasses.text} break-all`}>{device.hikgateway_url || t("accessControl.details.notConfigured")}</p>
                </div>
              )}
              <div>
                <p className={`text-[11px] ${labelColor} font-medium mb-0.5`}>{t("accessControl.connection.lastInteraction")}</p>
                <p className={`text-sm ${themeClasses.text}`}>
                  {firestoreInstantToIso(device.last_event_at) ? (
                    <>
                      {formatRelativeTime(device.last_event_at, t)}
                      <span className={`ml-1.5 text-xs ${themeClasses.textSecondary}`}>
                        ({new Date(firestoreInstantToIso(device.last_event_at)!).toLocaleString("es-MX")})
                      </span>
                    </>
                  ) : (
                    t("accessControl.relativeTime.noRecord")
                  )}
                </p>
              </div>
              {isRelay && (
                <div>
                  <p className={`text-[11px] ${labelColor} font-medium mb-0.5`}>{t("accessControl.connection.relayStatus")}</p>
                  <p className={`text-sm ${themeClasses.text}`}>
                    {device.connection_status === "connected"
                      ? t("accessControl.connection.relayReporting", { time: formatRelativeTime(device.last_heartbeat_at, t) })
                      : device.connection_status === "disconnected"
                        ? `${t("accessControl.connection.relayDisconnected")}${device.connection_lost_at ? ` ${t("accessControl.connection.since", { time: formatRelativeTime(device.connection_lost_at, t) })}` : ""}`
                        : t("accessControl.connection.relayNoReports")}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ISAPI control panel — for direct & relay modes */}
          {usesIsapi && (
            <div className={`${surface} rounded-xl p-4 border ${isRelay ? "border-amber-500/20" : "border-blue-500/20"}`}>
              <p className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor} mb-3`}>
                <i className="ri-remote-control-line mr-1"></i>{isRelay ? t("accessControl.control.viaRelay") : t("accessControl.control.direct")}
              </p>

              {/* Test & webhook setup require backend → device reachability,
                  so they are only enabled in direct mode. In relay mode the
                  agent itself handles connectivity. */}
              {isDirect && (
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <button
                    onClick={handleTest}
                    disabled={busy !== null}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all border ${themeClasses.cardBorder} ${themeClasses.buttonText} ${themeClasses.buttonHover} disabled:opacity-50`}
                  >
                    {busy === "test" ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-pulse-line"></i>
                    )}
                    {t("accessControl.control.testConnection")}
                  </button>
                  <button
                    onClick={handleSetupWebhook}
                    disabled={busy !== null}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all border ${themeClasses.cardBorder} ${themeClasses.buttonText} ${themeClasses.buttonHover} disabled:opacity-50`}
                  >
                    {busy === "webhook" ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-broadcast-line"></i>
                    )}
                    {t("accessControl.control.setupWebhook")}
                  </button>
                </div>
              )}

              {/* Un par Abrir/Cerrar por pluma. Con varias puertas en un solo
                  documento (Acuapue: salida = puerta 1, entrada = puerta 2) el
                  par único abría siempre door_no y la entrada no se podía
                  levantar desde acá. */}
              {controlPairs.map((pair) => (
                <div key={pair.door ?? "single"} className="mb-2 last:mb-0">
                  {pair.label && (
                    <p className={`text-[11px] font-medium ${labelColor} mb-1`}>{pair.label}</p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleControl("open", pair.door)}
                      disabled={busy !== null}
                      className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all bg-green-500/15 hover:bg-green-500/25 text-green-400 border border-green-500/30 disabled:opacity-50"
                    >
                      {busy === `open:${pair.door ?? device.door_no}` ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <i className="ri-door-open-line text-base"></i>
                      )}
                      {t("accessControl.control.openDoor")}
                    </button>
                    <button
                      onClick={() => handleControl("close", pair.door)}
                      disabled={busy !== null}
                      className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/30 disabled:opacity-50"
                    >
                      {busy === `close:${pair.door ?? device.door_no}` ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <i className="ri-door-closed-line text-base"></i>
                      )}
                      {t("accessControl.control.closeDoor")}
                    </button>
                  </div>
                </div>
              ))}

              <p className={`text-[11px] ${labelColor} mt-2.5`}>
                {isRelay
                  ? t("accessControl.control.relayHint")
                  : t("accessControl.control.directHint")}
              </p>
            </div>
          )}

          {/* Controller event log — opens the read-only records screen that
              reads the controller's stored history directly from the on-site
              bridge (does not touch the cloud DB). */}
          <button
            onClick={() => {
              onClose();
              const qs = new URLSearchParams({
                deviceId: device.id,
                serial: device.device_serial || "",
                ip: device.ip_address || "",
                name: device.device_name || "",
                yard: device.yard_name || "",
              });
              navigate(`/truck-yard/access-records?${qs.toString()}`);
            }}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all border ${themeClasses.cardBorder} ${themeClasses.buttonHover} ${themeClasses.text}`}
          >
            <i className="ri-history-line text-base"></i>
            Ver registros del controlador
          </button>

          {/* Timestamps */}
          <div className="flex gap-4 text-xs">
            <div>
              <span className={labelColor}>{t("accessControl.details.created")}: </span>
              <span className={themeClasses.text}>{fechaCorta(device.createdAt)}</span>
            </div>
            <div>
              <span className={labelColor}>{t("accessControl.details.updated")}: </span>
              <span className={themeClasses.text}>{fechaCorta(device.updatedAt)}</span>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-between px-6 py-4 border-t border-gray-700/30 bg-inherit rounded-b-2xl">
          <button
            onClick={() => onDelete(device)}
            className="px-4 py-2 rounded-xl text-red-400 hover:bg-red-500/10 font-medium text-sm transition-colors"
          >
            <i className="ri-delete-bin-line mr-1"></i>
            {t("common.delete")}
          </button>
          <div className="flex gap-2">
            <button onClick={onClose}
              className={`px-5 py-2.5 rounded-xl ${themeClasses.buttonHover} ${themeClasses.buttonText} font-medium text-sm transition-all border ${themeClasses.cardBorder}`}>
              {t("accessControl.details.close")}
            </button>
            <button
              onClick={() => { onClose(); onEdit(device); }}
              className="px-5 py-2.5 rounded-xl font-medium text-sm transition-colors"
              style={{ backgroundColor: brand.primary, color: brand.accent }}
            >
              <i className="ri-pencil-line mr-1"></i>
              {t("common.edit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Form Modal ──────────────────────────────────────────────────────────────

function DeviceFormModal({
  themeClasses,
  isDarkMode,
  isEdit,
  form: initialForm,
  yards,
  onClose,
  onSubmit,
  submitting,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  isEdit: boolean;
  form: DeviceFormData;
  yards: TruckYardOption[];
  onClose: () => void;
  onSubmit: (data: DeviceFormData) => void;
  submitting: boolean;
}) {
  const { t } = useTranslation("truckYard");
  const [selectedPreset, setSelectedPreset] = useState<string>(
    isEdit ? "custom" : "K1T341CMF-relay",
  );
  const [form, setForm] = useState<DeviceFormData>(() => {
    if (isEdit) return initialForm;
    const preset = DEVICE_MODEL_PRESETS.find((p) => p.id === "K1T341CMF-relay");
    return { ...initialForm, ...(preset?.defaults ?? {}) };
  });
  const [errors, setErrors] = useState<Partial<Record<keyof DeviceFormData, string>>>({});

  const applyPreset = (presetId: string) => {
    setSelectedPreset(presetId);
    const preset = DEVICE_MODEL_PRESETS.find((p) => p.id === presetId);
    if (!preset || presetId === "custom") return;
    setForm((prev) => ({ ...prev, ...preset.defaults }));
    setErrors({});
  };

  const surface = isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-50";
  const inputCls = `w-full px-3 py-2.5 ${surface} border border-transparent rounded-xl ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm`;
  const labelCls = `text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`;

  const validate = (): boolean => {
    const e: Partial<Record<keyof DeviceFormData, string>> = {};
    if (!form.device_serial.trim()) e.device_serial = t("accessControl.form.errors.required");
    if (!form.device_name.trim()) e.device_name = t("accessControl.form.errors.required");
    if (!form.yard_id) e.yard_id = t("accessControl.form.errors.selectYard");
    if (!form.door_no || Number(form.door_no) < 1) e.door_no = t("accessControl.form.errors.doorMin");
    const needsIsapi =
      form.connection_mode === "direct" || form.connection_mode === "relay";
    if (needsIsapi) {
      if (!form.ip_address) e.ip_address = t("accessControl.form.errors.ipRequired");
      if (!form.username) e.username = t("accessControl.form.errors.required");
      if (!isEdit && !form.password) e.password = t("accessControl.form.errors.required");
    }
    if (form.ip_address && !/^(\d{1,3}\.){3}\d{1,3}$/.test(form.ip_address)) {
      e.ip_address = t("accessControl.form.errors.ipInvalid");
    }
    if (form.hikgateway_url && !form.hikgateway_url.startsWith("http")) {
      e.hikgateway_url = t("accessControl.form.errors.urlInvalid");
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    if (validate()) onSubmit(form);
  };

  const setField = <K extends keyof DeviceFormData>(key: K, value: DeviceFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  // ── Reader map editors ──────────────────────────────────────────────────
  const addReader = () => {
    setForm((prev) => {
      const nextNo = prev.readers.reduce((m, r) => Math.max(m, Number(r.reader_no) || 0), 0) + 1;
      const direction = prev.readers.some((r) => r.direction === "entry") ? "exit" : "entry";
      return { ...prev, readers: [...prev.readers, { reader_no: nextNo, name: "", direction }] };
    });
  };
  const updateReader = (idx: number, patch: Partial<HikvisionReader>) => {
    setForm((prev) => ({
      ...prev,
      readers: prev.readers.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };
  const removeReader = (idx: number) => {
    setForm((prev) => ({ ...prev, readers: prev.readers.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto`}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>
            {isEdit ? t("accessControl.form.editTitle") : t("accessControl.form.newTitle")}
          </h2>
          <button onClick={onClose} className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}>
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Section: Model preset — only when creating */}
          {!isEdit && (
            <div>
              <p className={`text-[11px] uppercase tracking-widest font-semibold ${isDarkMode ? "text-gray-500" : "text-gray-400"} mb-3`}>
                <i className="ri-cpu-line mr-1"></i>{t("accessControl.form.deviceModel")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {DEVICE_MODEL_PRESETS.map((preset) => {
                  const active = selectedPreset === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => applyPreset(preset.id)}
                      className={`text-left px-3 py-2.5 rounded-xl border-2 transition-all ${
                        active
                          ? "border-primary-500 bg-primary-500/10"
                          : `border-transparent ${surface}`
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <i
                          className={`${preset.id === "custom" ? "ri-settings-3-line" : "ri-shield-check-line"} text-sm ${active ? "text-primary-400" : themeClasses.textSecondary}`}
                        />
                        <span
                          className={`text-sm font-medium ${active ? "text-primary-400" : themeClasses.text}`}
                        >
                          {t(preset.label)}
                        </span>
                      </div>
                      <p className={`text-[11px] ${themeClasses.textSecondary}`}>
                        {t(preset.description)}
                      </p>
                    </button>
                  );
                })}
              </div>
              <p className={`text-xs ${themeClasses.textSecondary} mt-2`}>
                {t("accessControl.form.presetHint")}
              </p>
            </div>
          )}

          {/* Section: Device info */}
          <div>
            <p className={`text-[11px] uppercase tracking-widest font-semibold ${isDarkMode ? "text-gray-500" : "text-gray-400"} mb-3`}>
              <i className="ri-device-line mr-1"></i>{t("accessControl.form.deviceInfo")}
            </p>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>{t("accessControl.form.deviceName")} <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.device_name}
                  onChange={(e) => setField("device_name", e.target.value)}
                  className={`${inputCls} ${errors.device_name ? "!border-red-500/60" : ""}`}
                  placeholder={t("accessControl.form.deviceNamePlaceholder")}
                />
                {errors.device_name && <span className="text-red-400 text-xs mt-1">{errors.device_name}</span>}
              </div>

              <div>
                <label className={labelCls}>{t("accessControl.form.serial")} <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.device_serial}
                  onChange={(e) => setField("device_serial", e.target.value)}
                  className={`${inputCls} font-mono ${errors.device_serial ? "!border-red-500/60" : ""}`}
                  placeholder="Ej: DS-K1T671M-3XF"
                  disabled={isEdit}
                />
                {errors.device_serial && <span className="text-red-400 text-xs mt-1">{errors.device_serial}</span>}
                {isEdit && <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>{t("accessControl.form.serialLocked")}</p>}
              </div>
            </div>
          </div>

          {/* Section: Location */}
          <div>
            <p className={`text-[11px] uppercase tracking-widest font-semibold ${isDarkMode ? "text-gray-500" : "text-gray-400"} mb-3`}>
              <i className="ri-map-pin-line mr-1"></i>{t("accessControl.form.location")}
            </p>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>{t("accessControl.list.yard")} <span className="text-red-500">*</span></label>
                <select
                  value={form.yard_id}
                  onChange={(e) => setField("yard_id", e.target.value)}
                  className={`${inputCls} ${errors.yard_id ? "!border-red-500/60" : ""}`}
                >
                  <option value="">{t("accessControl.form.selectYard")}</option>
                  {yards.map((y) => (
                    <option key={y.id} value={y.id}>{y.name}</option>
                  ))}
                </select>
                {errors.yard_id && <span className="text-red-400 text-xs mt-1">{errors.yard_id}</span>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t("accessControl.form.doorNumber")} <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min={1}
                    value={form.door_no}
                    onChange={(e) => setField("door_no", e.target.value)}
                    className={`${inputCls} ${errors.door_no ? "!border-red-500/60" : ""}`}
                  />
                  {errors.door_no && <span className="text-red-400 text-xs mt-1">{errors.door_no}</span>}
                </div>

                <div>
                  <label className={labelCls}>{t("accessControl.form.doorTypeLabel")} <span className="text-red-500">*</span></label>
                  <select
                    value={form.door_type}
                    onChange={(e) => setField("door_type", e.target.value as DeviceFormData["door_type"])}
                    className={inputCls}
                  >
                    <option value="entry">{t("accessControl.doorType.entry")}</option>
                    <option value="exit">{t("accessControl.doorType.exit")}</option>
                    <option value="bidirectional">{t("accessControl.doorType.bidirectional")}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Readers (reader_no → entry/exit direction) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className={`text-[11px] uppercase tracking-widest font-semibold ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                <i className="ri-scan-2-line mr-1"></i>{t("accessControl.form.readersTitle")}
              </p>
              <button
                type="button"
                onClick={addReader}
                className="text-xs font-medium text-primary-400 hover:text-primary-300 flex items-center gap-1"
              >
                <i className="ri-add-line"></i>{t("accessControl.form.addReader")}
              </button>
            </div>
            <p className={`text-[11px] ${themeClasses.textSecondary} mb-3`}>{t("accessControl.form.readersHint")}</p>

            {form.readers.length === 0 ? (
              <p className={`text-xs ${themeClasses.textSecondary} italic px-3 py-2.5 ${surface} rounded-xl`}>
                {t("accessControl.form.noReaders")}
              </p>
            ) : (
              <div className="space-y-2">
                {form.readers.map((r, idx) => (
                  <div key={idx} className={`flex items-center gap-2 px-3 py-2.5 ${surface} rounded-xl`}>
                    <div className="w-16 flex-shrink-0">
                      <label className={`${labelCls} !mb-1`}>{t("accessControl.form.readerNo")}</label>
                      <input
                        type="number"
                        min={1}
                        value={r.reader_no}
                        onChange={(e) => updateReader(idx, { reader_no: Number(e.target.value) })}
                        className={`w-full px-2 py-1.5 ${isDarkMode ? "bg-[#1f1f1f]" : "bg-white"} border border-transparent rounded-lg ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 outline-none text-sm font-mono`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className={`${labelCls} !mb-1`}>{t("accessControl.form.readerName")}</label>
                      <input
                        type="text"
                        value={r.name}
                        onChange={(e) => updateReader(idx, { name: e.target.value })}
                        placeholder={t("accessControl.form.readerNamePlaceholder")}
                        className={`w-full px-2 py-1.5 ${isDarkMode ? "bg-[#1f1f1f]" : "bg-white"} border border-transparent rounded-lg ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 outline-none text-sm`}
                      />
                    </div>
                    <div className="w-28 flex-shrink-0">
                      <label className={`${labelCls} !mb-1`}>{t("accessControl.form.readerDirection")}</label>
                      <select
                        value={r.direction}
                        onChange={(e) => updateReader(idx, { direction: e.target.value as HikvisionReader["direction"] })}
                        className={`w-full px-2 py-1.5 ${isDarkMode ? "bg-[#1f1f1f]" : "bg-white"} border border-transparent rounded-lg ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 outline-none text-sm`}
                      >
                        <option value="entry">{t("accessControl.doorType.entry")}</option>
                        <option value="exit">{t("accessControl.doorType.exit")}</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeReader(idx)}
                      className="self-end p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                      title={t("common.delete")}
                    >
                      <i className="ri-delete-bin-line text-base"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section: Connection */}
          <div>
            <p className={`text-[11px] uppercase tracking-widest font-semibold ${isDarkMode ? "text-gray-500" : "text-gray-400"} mb-3`}>
              <i className="ri-wifi-line mr-1"></i>{t("accessControl.form.connection")}
            </p>
            <div className="space-y-4">
              {/* Connection mode toggle */}
              <div>
                <label className={labelCls}>{t("accessControl.form.connectionMode")}</label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { mode: "direct", icon: "ri-plug-line", label: t("accessControl.connection.directBadge") },
                    { mode: "relay", icon: "ri-links-line", label: t("accessControl.connection.relayBadge") },
                    { mode: "hikgateway", icon: "ri-cloud-line", label: t("accessControl.connection.hikgatewayBadge") },
                  ] as const).map(({ mode, icon, label }) => {
                    const active = form.connection_mode === mode;
                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setField("connection_mode", mode)}
                        className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl border-2 transition-all ${
                          active
                            ? "border-primary-500 bg-primary-500/10"
                            : `border-transparent ${surface}`
                        }`}
                      >
                        <i className={`${icon} text-lg ${active ? "text-primary-400" : themeClasses.textSecondary}`}></i>
                        <span className={`text-xs font-medium ${active ? "text-primary-400" : themeClasses.textSecondary}`}>{label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className={`text-xs ${themeClasses.textSecondary} mt-1.5`}>
                  {form.connection_mode === "hikgateway"
                    ? t("accessControl.form.hikgatewayModeHint")
                    : form.connection_mode === "relay"
                      ? t("accessControl.form.relayModeHint")
                      : t("accessControl.form.directModeHint")}
                </p>
              </div>

              {/* HikGateway URL — only shown when mode = hikgateway */}
              {form.connection_mode === "hikgateway" && (
                <div>
                  <label className={labelCls}>{t("accessControl.form.hikgatewayUrl")}</label>
                  <input
                    type="text"
                    value={form.hikgateway_url}
                    onChange={(e) => setField("hikgateway_url", e.target.value)}
                    className={`${inputCls} font-mono text-xs ${errors.hikgateway_url ? "!border-red-500/60" : ""}`}
                    placeholder="Ej: http://192.168.1.50:8090"
                  />
                  {errors.hikgateway_url && <span className="text-red-400 text-xs mt-1">{errors.hikgateway_url}</span>}
                </div>
              )}

              {/* Device IP — always shown */}
              <div>
                <label className={labelCls}>
                  {t("accessControl.form.deviceIp")}
                  {(form.connection_mode === "direct" || form.connection_mode === "relay") && <span className="text-red-500"> *</span>}
                </label>
                <input
                  type="text"
                  value={form.ip_address}
                  onChange={(e) => setField("ip_address", e.target.value)}
                  className={`${inputCls} font-mono ${errors.ip_address ? "!border-red-500/60" : ""}`}
                  placeholder={form.connection_mode === "relay" ? "Ej: 192.168.1.100 (LAN)" : "Ej: 192.168.1.100"}
                />
                {errors.ip_address && <span className="text-red-400 text-xs mt-1">{errors.ip_address}</span>}
                {form.connection_mode === "relay" && (
                  <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                    {t("accessControl.form.relayIpHint")}
                  </p>
                )}
              </div>

              {/* Direct & Relay mode credentials (both need them to talk to the device) */}
              {(form.connection_mode === "direct" || form.connection_mode === "relay") && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>{t("accessControl.connection.username")} <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={form.username}
                        onChange={(e) => setField("username", e.target.value)}
                        className={`${inputCls} ${errors.username ? "!border-red-500/60" : ""}`}
                        placeholder="admin"
                        autoComplete="off"
                      />
                      {errors.username && <span className="text-red-400 text-xs mt-1">{errors.username}</span>}
                    </div>
                    <div>
                      <label className={labelCls}>{t("accessControl.connection.port")}</label>
                      <input
                        type="number"
                        value={form.device_port}
                        onChange={(e) => setField("device_port", e.target.value)}
                        className={`${inputCls} font-mono`}
                        placeholder="80"
                        min={1}
                        max={65535}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{t("accessControl.form.password")} <span className="text-red-500">*</span></label>
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => setField("password", e.target.value)}
                      className={`${inputCls} ${errors.password ? "!border-red-500/60" : ""}`}
                      placeholder={isEdit ? t("accessControl.form.passwordUnchanged") : t("accessControl.form.passwordPlaceholder")}
                      autoComplete="new-password"
                    />
                    {errors.password && <span className="text-red-400 text-xs mt-1">{errors.password}</span>}
                    <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                      {t("accessControl.form.passwordHint")}
                    </p>
                  </div>
                </>
              )}

              <div>
                <label className={labelCls}>{t("accessControl.form.status")}</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setField("status", "online")}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-all ${
                      form.status === "online"
                        ? "border-green-500 bg-green-500/10"
                        : `border-transparent ${surface}`
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${form.status === "online" ? "bg-green-400" : isDarkMode ? "bg-gray-600" : "bg-gray-300"}`} />
                    <span className={`text-sm font-medium ${form.status === "online" ? "text-green-400" : themeClasses.textSecondary}`}>{t("accessControl.form.online")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setField("status", "offline")}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-all ${
                      form.status === "offline"
                        ? "border-red-500 bg-red-500/10"
                        : `border-transparent ${surface}`
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${form.status === "offline" ? "bg-red-400" : isDarkMode ? "bg-gray-600" : "bg-gray-300"}`} />
                    <span className={`text-sm font-medium ${form.status === "offline" ? "text-red-400" : themeClasses.textSecondary}`}>{t("accessControl.form.offline")}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2.5 rounded-xl ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} font-medium text-sm transition-colors`}
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors disabled:opacity-60"
              style={{ backgroundColor: brand.primary, color: brand.accent }}
            >
              {submitting ? (
                <><i className="ri-loader-4-line animate-spin mr-1"></i>{t("accessControl.form.saving")}</>
              ) : isEdit ? (
                <><i className="ri-save-line mr-1"></i>{t("accessControl.form.saveChanges")}</>
              ) : (
                <><i className="ri-add-line mr-1"></i>{t("accessControl.form.createButton")}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Pagination ──────────────────────────────────────────────────────────────

function ControlPointsPagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}) {
  const { t } = useTranslation("truckYard");
  if (totalPages <= 1) return null;
  return (
    <div className="flex justify-center mt-6 items-center gap-2">
      <button onClick={() => onPageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
        className="p-1.5 rounded-lg bg-gray-200 dark:bg-dark-card text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-300 dark:hover:bg-dark-surface transition-colors">
        <i className="ri-arrow-left-s-line text-sm"></i>
      </button>
      <span className="text-sm text-gray-500">{t("common.page", { current: currentPage, total: totalPages })}</span>
      <button onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}
        className="p-1.5 rounded-lg bg-gray-200 dark:bg-dark-card text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-300 dark:hover:bg-dark-surface transition-colors">
        <i className="ri-arrow-right-s-line text-sm"></i>
      </button>
    </div>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState({ themeClasses, onAdd }: { themeClasses: ThemeClasses; onAdd: () => void }) {
  const { t } = useTranslation("truckYard");
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${themeClasses.cardBg}`}>
        <i className={`ri-door-lock-line text-4xl ${themeClasses.textSecondary}`}></i>
      </div>
      <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`}>{t("accessControl.empty.title")}</h3>
      <p className={`text-sm ${themeClasses.textSecondary} mb-4 text-center max-w-xs`}>
        {t("accessControl.empty.subtitle")}
      </p>
      <button onClick={onAdd} className="px-4 py-2 rounded-lg font-medium text-sm transition-colors" style={{ backgroundColor: brand.primary, color: brand.accent }}>
        <i className="ri-add-line mr-1"></i>{t("accessControl.empty.action")}
      </button>
    </div>
  );
}

// ─── Loading Skeleton ────────────────────────────────────────────────────────

function LoadingSkeleton({ isDarkMode, view }: { isDarkMode: boolean; view: "grid" | "list" | "console" }) {
  const base = isDarkMode ? "bg-dark-card" : "bg-gray-200";
  const shimmer = isDarkMode ? "bg-dark-surface" : "bg-gray-300";

  if (view !== "list") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`${base} rounded-2xl p-5 animate-pulse`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-11 h-11 rounded-xl ${shimmer}`} />
              <div className="flex-1 space-y-2">
                <div className={`h-3 w-3/4 rounded ${shimmer}`} />
                <div className={`h-2.5 w-1/2 rounded ${shimmer}`} />
              </div>
            </div>
            <div className={`rounded-xl p-3 space-y-2 mb-4 ${shimmer}/30`}>
              <div className={`h-2.5 w-2/3 rounded ${shimmer}`} />
              <div className={`h-2.5 w-1/2 rounded ${shimmer}`} />
            </div>
            <div className={`h-8 rounded-xl ${shimmer}`} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={`${base} rounded-xl p-4 animate-pulse flex items-center gap-4`}>
          <div className={`w-10 h-10 rounded-xl ${shimmer}`} />
          <div className="flex-1 space-y-2">
            <div className={`h-3 w-1/3 rounded ${shimmer}`} />
            <div className={`h-2.5 w-1/4 rounded ${shimmer}`} />
          </div>
          <div className={`h-6 w-16 rounded-full ${shimmer}`} />
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function AccessControlSection() {
  const { t } = useTranslation("truckYard");
  const { isDarkMode } = useDarkMode();

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  // Store
  const {
    hikvisionDevices: devices,
    devicesLoading: loading,
    setHikvisionDevices,
    setDevicesLoading,
  } = useHikvisionAccessStore();

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDoorType, setSelectedDoorType] = useState("");
  const [selectedYard, setSelectedYard] = useState("");
  const [selectedView, setSelectedView] = useState<"grid" | "list" | "console">("console");
  const [currentPage, setCurrentPage] = useState(1);
  const [detailsDevice, setDetailsDevice] = useState<HikvisionDevice | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editDevice, setEditDevice] = useState<HikvisionDevice | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // TODO: Replace with real yard data from yards API
  const [yards, setYards] = useState<TruckYardOption[]>([]);

  // Devices currently being probed for real reachability. While a device ID is
  // in this set, its card badge renders "Verificando…" instead of the stored
  // status. Only "direct"-mode devices are probeable via /hikvisionDevice/test.
  const [probingIds, setProbingIds] = useState<Set<string>>(new Set());

  // Probe reachability of all direct-mode devices in parallel and update each
  // device's status in the store as its probe resolves. When `silent` is
  // false the badge shows "Verificando…" during the probe; when true the
  // refresh is invisible — used by the 15 s background poll so the badge
  // doesn't flicker amber every tick.
  const probeDevices = useCallback(
    async (
      devices: HikvisionDevice[],
      silent: boolean,
    ): Promise<Record<string, "online" | "offline">> => {
      const probeable = devices.filter(
        (d) => d.connection_mode === "direct",
      );
      const statusMap: Record<string, "online" | "offline"> = {};
      if (probeable.length === 0) return statusMap;

      if (!silent) {
        setProbingIds(new Set(probeable.map((d) => d.id)));
      }

      await Promise.all(
        probeable.map(async (device) => {
          let newStatus: "online" | "offline" = "offline";
          try {
            const probeResult = await testHikvisionDevice(device.id);
            newStatus = probeResult.success ? "online" : "offline";
          } catch {
            newStatus = "offline";
          }
          statusMap[device.id] = newStatus;
          setHikvisionDevices(
            useHikvisionAccessStore
              .getState()
              .hikvisionDevices.map((d) =>
                d.id === device.id ? { ...d, status: newStatus } : d,
              ),
          );
          if (!silent) {
            setProbingIds((prev) => {
              const next = new Set(prev);
              next.delete(device.id);
              return next;
            });
          }
        }),
      );
      return statusMap;
    },
    [setHikvisionDevices],
  );

  // Fetch devices on mount and run an initial probe so the badge reflects
  // actual reachability instead of the stale stored value.
  const loadDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const result = await fetchHikvisionDevices({ limit: 100 });
      setHikvisionDevices(result.data);
      await probeDevices(result.data, false);
    } catch (err) {
      console.error("Error loading devices:", err);
    } finally {
      setDevicesLoading(false);
    }
  }, [setHikvisionDevices, setDevicesLoading, probeDevices]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Refetch silencioso cada 30 s (refetchInterval) para refrescar campos de
  // heartbeat (connection_status, last_heartbeat_at, last_event_at) sin recarga
  // manual. `initialData` + `staleTime` evitan un fetch en el montaje (de eso ya
  // se encarga `loadDevices`, que además hace el probe inicial); el primer
  // refetch ocurre a los 30 s. Pausa con form abierto y con pestaña oculta.
  const devicesPollQuery = useQuery({
    queryKey: ["hikvisionDevices", { limit: 100 }],
    queryFn: () => fetchHikvisionDevices({ limit: 100 }),
    initialData: () => ({
      data: useHikvisionAccessStore.getState().hikvisionDevices,
    }) as Awaited<ReturnType<typeof fetchHikvisionDevices>>,
    staleTime: 30000,
    enabled: !formOpen,
    refetchInterval: formOpen ? false : 30000,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (devicesPollQuery.isFetched && devicesPollQuery.data) {
      setHikvisionDevices(devicesPollQuery.data.data);
    }
  }, [devicesPollQuery.data, devicesPollQuery.isFetched, setHikvisionDevices]);

  // Re-probe en segundo plano cada 15 s (refetchInterval): vuelve a chequear los
  // dispositivos en modo `direct` para que un equipo que cae y vuelve pase de
  // rojo a verde sin recarga. `probeDevices` ya escribe el status en el store y
  // además retorna el mapa. Pausa con form/modal abiertos y con pestaña oculta.
  useQuery({
    queryKey: ["hikvisionProbe"],
    queryFn: async () => {
      const currentDevices =
        useHikvisionAccessStore.getState().hikvisionDevices;
      if (currentDevices.length === 0) return {};
      return probeDevices(currentDevices, true);
    },
    enabled: !formOpen && !detailsDevice,
    refetchInterval: 15000,
    refetchIntervalInBackground: false,
  });

  // Fetch yards for dropdown
  useEffect(() => {
    const loadYards = async () => {
      try {
        // Use the main TDC API to get yards the user has access to
        setYards(await fetchTruckYardOptions());
      } catch {
        // If no yard endpoint, leave empty — user can type ID manually
        setYards([]);
      }
    };
    loadYards();
  }, []);

  // Filter
  const filtered = devices.filter((d) => {
    const matchSearch =
      !searchQuery ||
      d.device_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.device_serial.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (d.yard_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (d.ip_address || "").includes(searchQuery);
    const matchType = !selectedDoorType || d.door_type === selectedDoorType;
    const matchYard = !selectedYard || d.yard_id === selectedYard;
    return matchSearch && matchType && matchYard;
  });

  // Console view paginates over controllers (grouped door docs); grid/list
  // paginate over individual door docs.
  const isConsoleView = selectedView === "console";
  const consoleGroups = groupByConsole(filtered);
  const pageItemCount = isConsoleView ? consoleGroups.length : filtered.length;
  const totalPages = Math.max(1, Math.ceil(pageItemCount / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const paginatedConsoles = consoleGroups.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Handlers
  const handleOpenAdd = () => {
    setEditDevice(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (device: HikvisionDevice) => {
    setDetailsDevice(null);
    setEditDevice(device);
    setFormOpen(true);
  };

  const handleSubmit = async (data: DeviceFormData) => {
    setSubmitting(true);
    try {
      const port = data.device_port ? Number(data.device_port) : null;
      // Drop incomplete rows and coerce reader_no to a number.
      const readers = data.readers
        .map((r) => ({ reader_no: Number(r.reader_no), name: r.name || "", direction: r.direction }))
        .filter((r) => Number.isFinite(r.reader_no) && r.reader_no >= 1);
      if (editDevice) {
        const params: UpdateHikvisionDeviceParams = {
          id: editDevice.id,
          device_name: data.device_name,
          yard_id: data.yard_id,
          door_no: Number(data.door_no),
          door_type: data.door_type,
          readers,
          connection_mode: data.connection_mode,
          hikgateway_url: data.connection_mode === "hikgateway" ? (data.hikgateway_url || null) : null,
          ip_address: data.ip_address || null,
          username:
            data.connection_mode === "direct" || data.connection_mode === "relay"
              ? data.username || null
              : null,
          password:
            data.connection_mode === "direct" || data.connection_mode === "relay"
              ? data.password || null
              : null,
          device_port:
            data.connection_mode === "direct" || data.connection_mode === "relay"
              ? port
              : null,
          status: data.status,
        };
        await updateHikvisionDevice(params);
        Swal.fire({ icon: "success", title: t("accessControl.messages.updatedTitle"), text: t("accessControl.messages.updatedText"), timer: 2000, showConfirmButton: false });
      } else {
        const params: CreateHikvisionDeviceParams = {
          device_serial: data.device_serial,
          device_name: data.device_name,
          yard_id: data.yard_id,
          door_no: Number(data.door_no),
          door_type: data.door_type,
          readers,
          connection_mode: data.connection_mode,
          hikgateway_url: data.connection_mode === "hikgateway" ? (data.hikgateway_url || null) : null,
          ip_address: data.ip_address || null,
          username:
            data.connection_mode === "direct" || data.connection_mode === "relay"
              ? data.username || null
              : null,
          password:
            data.connection_mode === "direct" || data.connection_mode === "relay"
              ? data.password || null
              : null,
          device_port:
            data.connection_mode === "direct" || data.connection_mode === "relay"
              ? port
              : null,
          status: data.status,
        };
        await createHikvisionDevice(params);
        Swal.fire({ icon: "success", title: t("accessControl.messages.createdTitle"), text: t("accessControl.messages.createdText"), timer: 2000, showConfirmButton: false });
      }
      setFormOpen(false);
      setEditDevice(null);
      await loadDevices();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("accessControl.messages.unknownError");
      Swal.fire({ icon: "error", title: t("common.error"), text: message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (device: HikvisionDevice) => {
    const result = await Swal.fire({
      title: t("accessControl.messages.deleteTitle"),
      html: t("accessControl.messages.deleteHtml", { name: device.device_name, serial: device.device_serial }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("accessControl.messages.deleteConfirm"),
      cancelButtonText: t("common.cancel"),
      confirmButtonColor: "#ef4444",
    });

    if (result.isConfirmed) {
      try {
        await deleteHikvisionDevice(device.id);
        setDetailsDevice(null);
        await loadDevices();
        Swal.fire({ icon: "success", title: t("accessControl.messages.deletedTitle"), timer: 1500, showConfirmButton: false });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : t("accessControl.messages.unknownError");
        Swal.fire({ icon: "error", title: t("common.error"), text: message });
      }
    }
  };

  return (
    <div className="h-full flex flex-col relative">
      <ControlPointsHeader
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        searchQuery={searchQuery}
        selectedDoorType={selectedDoorType}
        selectedYard={selectedYard}
        yards={yards}
        selectedView={selectedView}
        onSearchChange={(v) => { setSearchQuery(v); setCurrentPage(1); }}
        onDoorTypeChange={(v) => { setSelectedDoorType(v); setCurrentPage(1); }}
        onYardChange={(v) => { setSelectedYard(v); setCurrentPage(1); }}
        onViewChange={(v) => { setSelectedView(v); setCurrentPage(1); }}
        onAdd={handleOpenAdd}
        total={filtered.length}
        loading={loading}
      />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {loading ? (
          <LoadingSkeleton isDarkMode={isDarkMode} view={selectedView} />
        ) : pageItemCount === 0 ? (
          <EmptyState themeClasses={themeClasses} onAdd={handleOpenAdd} />
        ) : isConsoleView ? (
          <div className="space-y-4">
            {paginatedConsoles.map((g) => (
              <ConsoleCard key={g.serial} group={g} themeClasses={themeClasses} isDarkMode={isDarkMode} probingIds={probingIds} onDetails={setDetailsDevice} onEdit={handleOpenEdit} />
            ))}
          </div>
        ) : selectedView === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginated.map((d) => (
              <DeviceGridCard key={d.id} device={d} themeClasses={themeClasses} isDarkMode={isDarkMode} probing={probingIds.has(d.id)} onDetails={setDetailsDevice} onEdit={handleOpenEdit} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {paginated.map((d) => (
              <DeviceListRow key={d.id} device={d} themeClasses={themeClasses} probing={probingIds.has(d.id)} onDetails={setDetailsDevice} onEdit={handleOpenEdit} />
            ))}
          </div>
        )}

        <ControlPointsPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </div>

      {/* Details Modal */}
      {detailsDevice && (
        <DeviceDetailsModal
          device={detailsDevice}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          probing={probingIds.has(detailsDevice.id)}
          onClose={() => setDetailsDevice(null)}
          onEdit={handleOpenEdit}
          onDelete={handleDelete}
        />
      )}

      {/* Form Modal */}
      {formOpen && (
        <DeviceFormModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          isEdit={!!editDevice}
          form={
            editDevice
              ? {
                  device_serial: editDevice.device_serial,
                  device_name: editDevice.device_name,
                  yard_id: editDevice.yard_id,
                  door_no: String(editDevice.door_no),
                  door_type: editDevice.door_type,
                  readers: Array.isArray(editDevice.readers) ? editDevice.readers : [],
                  connection_mode: editDevice.connection_mode || "hikgateway",
                  hikgateway_url: editDevice.hikgateway_url || "",
                  ip_address: editDevice.ip_address || "",
                  username: editDevice.username || "",
                  password: editDevice.password || "",
                  device_port: editDevice.device_port ? String(editDevice.device_port) : "80",
                  status: editDevice.status,
                }
              : EMPTY_FORM
          }
          yards={yards}
          onClose={() => { setFormOpen(false); setEditDevice(null); }}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      )}
    </div>
  );
}
